import React, { useState, useEffect, useRef } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const C = { teal:'#00B4D8', dark:'#1F2937', grey:'#6B7280', border:'#E5E7EB', gold:'#C9A84C' };

export default function SuperAdminCertificates() {
  const [name, setName] = useState('');
  const [sigFile, setSigFile] = useState(null);
  const [sigPreview, setSigPreview] = useState(null);
  const [hasExisting, setHasExisting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sigFont, setSigFont] = useState('Great Vibes');
  const fileRef = useRef();

  useEffect(() => {
    const id = 'sig-fonts';
    if (!document.getElementById(id)) {
      const st = document.createElement('style'); st.id = id;
      st.textContent = "@font-face{font-family:'Great Vibes';src:url('/fonts/GreatVibes.woff2') format('woff2');font-display:swap;}"
        + "@font-face{font-family:'Dancing Script';src:url('/fonts/DancingScript.woff2') format('woff2');font-display:swap;}";
      document.head.appendChild(st);
    }
  }, []);

  const SIG_FONTS = ['Great Vibes', 'Dancing Script'];

  const removeBackground = (file) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const maxW = 700; const scale = img.naturalWidth > maxW ? maxW / img.naturalWidth : 1;
      const cv = document.createElement('canvas');
      cv.width = Math.round(img.naturalWidth * scale); cv.height = Math.round(img.naturalHeight * scale);
      const ctx = cv.getContext('2d'); ctx.drawImage(img, 0, 0, cv.width, cv.height);
      const d = ctx.getImageData(0, 0, cv.width, cv.height); const a = d.data;
      const t1 = 0.32, t2 = 0.48;
      for (let i = 0; i < a.length; i += 4) {
        const lum = (0.299 * a[i] + 0.587 * a[i + 1] + 0.114 * a[i + 2]) / 255;
        let al; if (lum <= t1) al = 255; else if (lum >= t2) al = 0; else al = Math.round((t2 - lum) / (t2 - t1) * 255);
        if (al < 70) al = 0;
        a[i] = 21; a[i + 1] = 35; a[i + 2] = 74; a[i + 3] = al;
      }
      ctx.putImageData(d, 0, 0);
      cv.toBlob(b => b ? resolve(new File([b], 'signature.png', { type: 'image/png' })) : reject(), 'image/png');
    };
    img.onerror = reject; img.src = URL.createObjectURL(file);
  });

  const makeTypedSignature = async () => {
    const n = (name || '').trim();
    if (!n) { toast.error('Enter the Director name above first'); return; }
    try { await document.fonts.load(`64px "${sigFont}"`); await document.fonts.ready; } catch (e) {}
    const canvas = document.createElement('canvas'); canvas.width = 600; canvas.height = 170;
    const ctx = canvas.getContext('2d'); ctx.clearRect(0, 0, 600, 170);
    ctx.fillStyle = '#15234a'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    let size = 90; ctx.font = `${size}px "${sigFont}"`;
    while (ctx.measureText(n).width > 560 && size > 24) { size -= 4; ctx.font = `${size}px "${sigFont}"`; }
    ctx.fillText(n, 300, 90);
    canvas.toBlob(b => { if (!b) { toast.error('Could not render'); return; } setSigFile(new File([b], 'director-sig.png', { type: 'image/png' })); setSigPreview(URL.createObjectURL(b)); toast.success('Signature ready — click Save'); }, 'image/png');
  };

  useEffect(() => {
    api.get('/api/superadmin/certificates/settings').then(r => {
      setName(r.data.course_director_name && r.data.course_director_name !== 'Course Director' ? r.data.course_director_name : '');
      setHasExisting(r.data.has_signature);
    }).catch(() => {});
  }, []);

  const handleFile = async e => {
    const f = e.target.files[0]; if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error('Image too large — max 4MB'); return; }
    if (!f.type.startsWith('image/')) { toast.error('Select a PNG or JPG'); return; }
    try { const c = await removeBackground(f); setSigFile(c); setSigPreview(URL.createObjectURL(c)); toast.success('Background removed — only the signature kept'); }
    catch { setSigFile(f); setSigPreview(URL.createObjectURL(f)); }
  };

  const removeSignature = async () => {
    try { await api.delete('/api/superadmin/certificates/signature'); setSigFile(null); setSigPreview(null); setHasExisting(false); toast.success('Signature removed'); }
    catch { toast.error('Could not remove signature'); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('course_director_name', name || 'Course Director');
      if (sigFile) fd.append('signature', sigFile);
      await api.post('/api/superadmin/certificates/signature', fd);
      toast.success('Saved — applies to all partner certificates');
      if (sigFile) setHasExisting(true);
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const inp = { width:'100%', padding:'10px 12px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:14, boxSizing:'border-box' };

  return (
    <Layout role="superadmin">
      <div style={{ maxWidth:640 }}>
        <h2 style={{ margin:'0 0 6px', fontSize:22, fontWeight:800 }}>Certificate Co-Signature (Course Director)</h2>
        <p style={{ margin:'0 0 20px', color:C.grey }}>This is <strong>SpeakIQ's signature</strong>. It appears on the left of <strong>every partner school's certificate</strong> — set it once here.</p>

        <div className="card" style={{ padding:28, marginBottom:20 }}>
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            <div>
              <label style={{ fontSize:13, fontWeight:600, color:C.grey, display:'block', marginBottom:6 }}>Director Name</label>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. SpeakIQ Academic Director" style={inp} />
            </div>

            <div>
              <label style={{ fontSize:13, fontWeight:600, color:C.grey, display:'block', marginBottom:6 }}>Option A — Turn the name into a handwritten signature</label>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
                <span style={{ fontSize:12, color:C.grey }}>Style:</span>
                {SIG_FONTS.map(f => (
                  <button key={f} onClick={() => setSigFont(f)} style={{ padding:'6px 14px', borderRadius:8, border:`2px solid ${sigFont===f?C.teal:C.border}`, background:sigFont===f?'#EBF9FD':'#fff', cursor:'pointer', fontFamily:`'${f}', cursive`, fontSize:20, color:'#15234a' }}>
                    {(name || 'Signature').slice(0,12)}
                  </button>
                ))}
              </div>
              <div style={{ marginTop:10, padding:'16px', border:`1px dashed ${C.border}`, borderRadius:10, textAlign:'center', background:'#FAFAFA' }}>
                <span style={{ fontFamily:`'${sigFont}', cursive`, fontSize:42, color:'#15234a', lineHeight:1.2 }}>{name || 'Director name'}</span>
              </div>
              <button onClick={makeTypedSignature} style={{ marginTop:8, padding:'9px 18px', borderRadius:8, background:C.teal, color:'#fff', border:'none', fontWeight:700, fontSize:13, cursor:'pointer' }}>✍️ Use this as signature</button>
            </div>

            <div style={{ textAlign:'center', color:C.grey, fontSize:12, fontWeight:700 }}>— or —</div>

            <div>
              <label style={{ fontSize:13, fontWeight:600, color:C.grey, display:'block', marginBottom:6 }}>Option B — Upload a signature image <span style={{ fontWeight:400, marginLeft:8 }}>(background auto-removed)</span></label>
              <div onClick={() => fileRef.current.click()} style={{ border:`2px dashed ${sigPreview||hasExisting?C.gold:C.border}`, borderRadius:12, padding:24, textAlign:'center', cursor:'pointer', background:sigPreview||hasExisting?'#FFFBF0':'#FAFAFA', minHeight:100, display:'flex', alignItems:'center', justifyContent:'center' }}>
                {sigPreview ? <img src={sigPreview} alt="" style={{ maxHeight:80, maxWidth:'80%', objectFit:'contain' }} />
                  : hasExisting ? <div><div style={{ fontSize:24, marginBottom:6 }}>OK</div><div style={{ fontSize:13, color:C.grey }}>Signature uploaded — click to replace</div></div>
                  : <div><div style={{ fontSize:32, marginBottom:6 }}>+</div><div style={{ fontSize:14, fontWeight:600, color:C.dark }}>Upload Signature</div></div>}
              </div>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg" onChange={handleFile} style={{ display:'none' }} />
              {sigPreview ? (
                <button onClick={() => { setSigFile(null); setSigPreview(null); }} style={{ marginTop:8, fontSize:12, color:'#EF4444', background:'none', border:'none', cursor:'pointer' }}>Remove new selection</button>
              ) : hasExisting ? (
                <button onClick={removeSignature} style={{ marginTop:8, fontSize:12, color:'#EF4444', background:'none', border:'none', cursor:'pointer' }}>✕ Remove uploaded signature (use typed handwriting instead)</button>
              ) : null}
            </div>
          </div>
        </div>

        <div className="card" style={{ padding:24, marginBottom:20, background:'#FFFBF0', border:`1px solid ${C.gold}40` }}>
          <h4 style={{ margin:'0 0 12px', fontSize:14, fontWeight:700, color:C.dark }}>How it appears (left side of every certificate)</h4>
          <div style={{ textAlign:'center' }}>
            <div style={{ height:44, marginBottom:4, display:'flex', alignItems:'flex-end', justifyContent:'center' }}>
              {sigPreview ? <img src={sigPreview} alt="" style={{ maxHeight:40, maxWidth:140, objectFit:'contain' }} />
                : hasExisting ? <span style={{ fontSize:11, color:C.grey, fontStyle:'italic' }}>(your uploaded signature image)</span>
                : name ? <span style={{ fontFamily:`'${sigFont}', cursive`, fontSize:30, color:'#15234a' }}>{name}</span>
                : <span style={{ fontSize:11, color:C.grey, fontStyle:'italic' }}>(no signature — plain line)</span>}
            </div>
            <div style={{ borderTop:'1px solid #3A3A5A', paddingTop:6, width:160, margin:'0 auto' }}>
              <div style={{ fontSize:11, fontWeight:700 }}>Course Director</div>
              <div style={{ fontSize:10, color:C.grey, fontStyle:'italic' }}>SpeakIQ English Platform</div>
            </div>
          </div>
        </div>

        <button onClick={save} disabled={saving} className="btn-teal" style={{ width:'100%', padding:'13px', fontSize:15 }}>
          {saving ? 'Saving...' : 'Save Co-Signature'}
        </button>
        <p style={{ fontSize:12, color:C.grey, textAlign:'center', marginTop:8 }}>Applies to all partner schools' certificates. Leave blank for a plain "SpeakIQ English Platform" line.</p>
      </div>
    </Layout>
  );
}
